import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { Container } from '@beyond-silence/ui';
import { supabase } from '@/utils/supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Profile = Record<string, any> & { id: string; display_name: string | null; avatar_url: string | null; bio: string | null; title: string | null; public_slug: string | null; is_public: boolean; role: string; };

export function SettingsPage() {
  const { t } = useTranslation('staff');
  const { refreshProfile } = useOutletContext<{ refreshProfile: () => void }>();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [form, setForm] = useState({ display_name: '', avatar_url: '', bio: '', title: '', public_slug: '', is_public: false });

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: prof } = await (supabase.from('profiles') as any).select('*').eq('id', user.id).single();
      if (prof) {
        setProfile(prof);
        setForm({
          display_name: prof.display_name ?? '',
          avatar_url: prof.avatar_url ?? '',
          bio: prof.bio ?? '',
          title: prof.title ?? '',
          public_slug: prof.public_slug ?? '',
          is_public: prof.is_public ?? false,
        });
      }
      setLoading(false);
    })();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    setSaving(true); setMessage(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase.from('profiles') as any).update({
      display_name: form.display_name,
      avatar_url: form.avatar_url || null,
      bio: form.bio || null,
      title: form.title || null,
      public_slug: form.public_slug || null,
      is_public: form.is_public,
    }).eq('id', profile.id);
    if (error) { setMessage({ type: 'error', text: error.message }); }
    else {
      setMessage({ type: 'success', text: 'Profile updated. Refreshing…' });
      refreshProfile(); // Updates sidebar, dashboard, and everywhere else
    }
    setSaving(false);
  };

  if (loading) {
    return <Container size="dashboard" className="flex min-h-[40vh] items-center justify-center py-8"><div className="font-ui text-ui-base text-ink-light">Loading…</div></Container>;
  }

  const avatarFallback = form.display_name
    ? form.display_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';

  return (
    <Container size="dashboard" className="py-4 sm:py-6 md:py-8">
      <h1 className="font-ui text-ui-lg font-bold text-ink">Profile Settings</h1>

      {message && (
        <div className={`mt-3 rounded-md px-4 py-2 font-ui text-ui-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-primary/10 text-primary'}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSave} className="mt-6 space-y-6">
        {/* Avatar preview + URL */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
          <div className="shrink-0">
            {form.avatar_url ? (
              <img src={form.avatar_url} alt="" loading="lazy" decoding="async" className="h-16 w-16 rounded-full object-cover ring-2 ring-soft/40 sm:h-20 sm:w-20" />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary/20 font-ui text-xl font-bold text-secondary ring-2 ring-soft/40 sm:h-20 sm:w-20">
                {avatarFallback}
              </div>
            )}
          </div>
          <div className="flex-1 space-y-4">
            <div>
              <label className="block font-ui text-ui-sm font-medium text-ink">Avatar URL</label>
              <input
                type="url" value={form.avatar_url}
                onChange={e => setForm(f => ({ ...f, avatar_url: e.target.value }))}
                className="mt-1 w-full rounded-md border border-soft/60 bg-white px-3 py-2 font-mono text-ui-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="https://example.com/avatar.jpg"
              />
              <p className="mt-1 font-ui text-ui-xs text-ink-muted">Paste an image URL. Upload coming in a future update.</p>
            </div>
          </div>
        </div>

        {/* Name + Title */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block font-ui text-ui-sm font-medium text-ink">Display Name</label>
            <input
              type="text" value={form.display_name}
              onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
              required maxLength={120}
              className="mt-1 w-full rounded-md border border-soft/60 bg-white px-3 py-2 font-ui text-ui-base text-ink focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div>
            <label className="block font-ui text-ui-sm font-medium text-ink">Title</label>
            <input
              type="text" value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              maxLength={160} placeholder="e.g. Senior Correspondent"
              className="mt-1 w-full rounded-md border border-soft/60 bg-white px-3 py-2 font-ui text-ui-base text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        {/* Bio */}
        <div>
          <label className="block font-ui text-ui-sm font-medium text-ink">Bio</label>
          <textarea
            value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
            rows={4} maxLength={5000}
            className="mt-1 w-full rounded-md border border-soft/60 bg-white px-3 py-3 font-editorial-body text-body-md text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="Tell readers about yourself…"
          />
        </div>

        {/* Public slug + toggle */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block font-ui text-ui-sm font-medium text-ink">Public URL Slug</label>
            <input
              type="text" value={form.public_slug}
              onChange={e => setForm(f => ({ ...f, public_slug: e.target.value }))}
              className="mt-1 w-full rounded-md border border-soft/60 bg-white px-3 py-2 font-mono text-ui-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="your-name"
            />
            <p className="mt-1 font-ui text-ui-xs text-ink-muted">Used in URL: /author/your-name</p>
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox" checked={form.is_public}
                onChange={e => setForm(f => ({ ...f, is_public: e.target.checked }))}
                className="h-5 w-5 rounded border-soft/60 text-primary focus:ring-primary/30"
              />
              <span className="font-ui text-ui-sm text-ink">Public author profile</span>
            </label>
          </div>
        </div>

        {/* Save */}
        <div className="flex items-center gap-3 border-t border-soft/40 pt-4">
          <button
            type="submit" disabled={saving}
            className="rounded-pill bg-primary px-5 py-2.5 font-ui text-ui-base font-medium text-white transition-colors hover:bg-primary-600 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
          <p className="font-ui text-ui-xs text-ink-muted">
            Role: <span className="font-semibold text-ink">{profile?.role ?? 'reader'}</span>
          </p>
        </div>
      </form>
    </Container>
  );
}
